/**
 * Meals Service
 * 
 * Centralized API calls for meal-related operations.
 * Reduces duplication across App.js and components.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Get today's meals for a user
 * @param {string} userId 
 * @param {number} tzOffset - Optional timezone offset in hours from UTC. If not provided, uses browser detection.
 * @returns {Promise<{meals: array, totals: object, count: number}>}
 */
export const getTodayMeals = async (userId, tzOffset = null) => {
  // Use provided timezone offset, or fall back to browser detection
  const offset = tzOffset !== null ? tzOffset : Math.round(-new Date().getTimezoneOffset() / 60);
  const response = await fetch(`${BACKEND_URL}/api/meals/${userId}/today?tz_offset=${offset}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch meals: ${response.status}`);
  }
  return response.json();
};

/**
 * Get meal history for a user
 * @param {string} userId 
 * @param {number} days - Number of days to fetch (default 7)
 * @returns {Promise<array>}
 */
export const getMealHistory = async (userId, days = 7) => {
  const response = await fetch(`${BACKEND_URL}/api/meals/${userId}/history?days=${days}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch history: ${response.status}`);
  }
  return response.json();
};

/**
 * Log a new meal
 * @param {object} mealData 
 * @returns {Promise<object>}
 */
export const logMeal = async (mealData) => {
  const response = await fetch(`${BACKEND_URL}/api/meals`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(mealData)
  });
  if (!response.ok) {
    throw new Error(`Failed to log meal: ${response.status}`);
  }
  return response.json();
};

/**
 * Log a manual entry meal
 * @param {object} mealData 
 * @returns {Promise<object>}
 */
export const logManualMeal = async (mealData) => {
  // Add timezone offset to meal data
  const dataWithTimezone = {
    ...mealData,
    tz_offset: Math.round(-new Date().getTimezoneOffset() / 60)
  };
  
  const response = await fetch(`${BACKEND_URL}/api/meals/manual`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dataWithTimezone)
  });
  if (!response.ok) {
    throw new Error(`Failed to log manual meal: ${response.status}`);
  }
  return response.json();
};

/**
 * Delete a meal
 * @param {string} mealId 
 * @returns {Promise<object>}
 */
export const deleteMeal = async (mealId) => {
  const response = await fetch(`${BACKEND_URL}/api/meals/${mealId}`, {
    method: 'DELETE'
  });
  if (!response.ok) {
    throw new Error(`Failed to delete meal: ${response.status}`);
  }
  return response.json();
};

/**
 * Analyze food from text description
 * @param {string} description 
 * @param {string} userId - Optional user ID (empty for guests)
 * @returns {Promise<object>}
 */
export const analyzeText = async (description, userId = '') => {
  const response = await fetch(`${BACKEND_URL}/api/meals/analyze-text`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ description, user_id: userId })
  });
  if (!response.ok) {
    throw new Error(`Failed to analyze text: ${response.status}`);
  }
  return response.json();
};

/**
 * Analyze food from image (preliminary analysis)
 * @param {string} imageBase64 
 * @param {string} userId 
 * @param {string} description - Optional description hint
 * @returns {Promise<object>}
 */
export const analyzeImage = async (imageBase64, userId = '', description = '') => {
  const response = await fetch(`${BACKEND_URL}/api/meals/analyze/preliminary`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      image_base64: imageBase64,
      user_id: userId,
      description
    })
  });
  if (!response.ok) {
    throw new Error(`Failed to analyze image: ${response.status}`);
  }
  return response.json();
};

/**
 * Confirm and save analyzed meal
 * @param {string} userId 
 * @param {string} imageHash 
 * @param {string} description 
 * @returns {Promise<object>}
 */
export const confirmMeal = async (userId, imageHash, description = '') => {
  const response = await fetch(`${BACKEND_URL}/api/meals/analyze/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_id: userId,
      image_hash: imageHash,
      added_ingredients: description
    })
  });
  if (!response.ok) {
    throw new Error(`Failed to confirm meal: ${response.status}`);
  }
  return response.json();
};

/**
 * Import guest meals to user account
 * @param {string} userId 
 * @param {array} meals 
 * @param {object} profile - Optional guest profile
 * @returns {Promise<object>}
 */
export const importGuestMeals = async (userId, meals, profile = null) => {
  const response = await fetch(`${BACKEND_URL}/api/meals/import-guest-meals`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_id: userId,
      meals,
      profile
    })
  });
  if (!response.ok) {
    throw new Error(`Failed to import guest meals: ${response.status}`);
  }
  return response.json();
};

/**
 * Toggle meal as favorite
 * @param {string} mealId 
 * @param {string} userId 
 * @returns {Promise<object>}
 */
export const toggleFavorite = async (mealId, userId) => {
  const response = await fetch(`${BACKEND_URL}/api/meals/${mealId}/favorite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id: userId })
  });
  if (!response.ok) {
    throw new Error(`Failed to toggle favorite: ${response.status}`);
  }
  return response.json();
};

export default {
  getTodayMeals,
  getMealHistory,
  logMeal,
  logManualMeal,
  deleteMeal,
  analyzeText,
  analyzeImage,
  confirmMeal,
  importGuestMeals,
  toggleFavorite
};
